import { throttle } from 'es-toolkit/compat';

import { queryId, queryIdAll } from '@ably/ui/core/dom-query';

const mdBreakpoint = () => window.matchMedia('(min-width: 48rem)').matches;
const DRAG_BUFFER = 20;

const init = (slidesContainer: HTMLElement) => {
  const transformContainer = queryId('slider-strip', slidesContainer) as HTMLElement;
  const slides = Array.from(queryIdAll('slider-slide', slidesContainer)) ?? [];
  const slideLeftChevron = queryId('slider-previous', slidesContainer);
  const slideRightChevron = queryId('slider-next', slidesContainer);
  const slideMarkers = Array.from(queryIdAll('slider-marker', slidesContainer));
  const sliderControls = queryId('slider-controls', slidesContainer);

  sliderControls?.classList.replace('hidden', 'flex');
  const slidesLength = slides.length;

  const slidesWidth = slidesContainer.getBoundingClientRect().width;
  const { width: slideWidth, left: slideLeftDistance } = slides[0]?.getBoundingClientRect() ?? { width: 0, left: 0 };
  const { left: slideLeftDistanceSecond } = slides[1]?.getBoundingClientRect() ?? { left: 0 };
  const slideGap = slideLeftDistanceSecond - slideLeftDistance - slideWidth;
  const adjustment = (slidesWidth - slideWidth) / 2;

  let currentIndex = 0;
  let touchStartX = 0;

  const calculateTransform = (index: number) => index * -slideWidth + adjustment + index * -slideGap;

  const updateSlide = (index: number) => {
    if (transformContainer) {
      transformContainer.style.transform = `translateX(${calculateTransform(index)}px)`;
    }
  };

  const updateMarkers = (index: number) => {
    slideMarkers.forEach((marker) => marker.classList.remove('text-active-orange'));
    slideMarkers[index]?.classList.remove('text-cool-black');
    slideMarkers[index]?.classList.add('text-active-orange');
  };

  const slideLeft = () => {
    currentIndex = currentIndex - 1 <= 0 ? 0 : currentIndex - 1;
    updateSlide(currentIndex);
    updateMarkers(currentIndex);
  };

  const slideRight = () => {
    currentIndex = currentIndex + 1 >= slidesLength ? currentIndex : currentIndex + 1;
    updateSlide(currentIndex);
    updateMarkers(currentIndex);
  };

  updateSlide(0);
  updateMarkers(0);

  slideLeftChevron?.addEventListener('click', slideLeft);

  transformContainer?.addEventListener('touchstart', (e: TouchEvent) => {
    touchStartX = e.touches[0]?.clientX || 0;
  });

  transformContainer?.addEventListener('touchend', (e: TouchEvent) => {
    const distance = (e.changedTouches[0]?.clientX || 0) - touchStartX;

    // Prevent sliding on clicks
    if (Math.abs(distance) < DRAG_BUFFER) return;

    const direction = distance > 0 ? slideLeft : slideRight;
    direction();
  });

  slideRightChevron?.addEventListener('click', slideRight);

  return () => {
    if (transformContainer) {
      transformContainer.style.transform = '';
    }
    sliderControls?.classList.replace('flex', 'hidden');
  };
};

const Slider = ({ container, mqEnableThreshold }: { container: HTMLElement; mqEnableThreshold: () => boolean }) => {
  if (!container) return () => {};

  const breakpointCheck = mqEnableThreshold || (() => !mdBreakpoint());

  let unmount = () => {};

  const handleResize = throttle(() => {
    // Call previous unmount to clean up resources
    unmount();

    if (breakpointCheck()) {
      unmount = init(container);
    } else {
      unmount = () => {};
    }
  }, 100);

  // Initial setup
  if (breakpointCheck()) {
    unmount = init(container);
  }

  // Add resize listener once
  window.addEventListener('resize', handleResize);

  // Return cleanup function that removes event listener and calls unmount
  return () => {
    window.removeEventListener('resize', handleResize);
    unmount();
  };
};

export default Slider;
